import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  loading: false,
  isLoggedIn: true,
  authToken: null,
  error: null,
  user: null,
  data: null,
  // A sign-in waiting for its second step: { challenge, method: "sms" | "app",
  // destination, message }. The server holds the session until the code is right.
  twoFactor: null,
  _subdomain: null, // for overriding subdomain
  loginWithEmail: {
    data: null,
    error: null,
    loading: false,
  },
  handleUnAuthorized: {
    redirectToLogin: false,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginUserStart: (state) => {
      state.loading = true;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = null;
      state.user = null;
      state.data = null;
      state.twoFactor = null;
      state._subdomain = null;
    },
    twoFactorRequired: (state, action) => {
      state.loading = false;
      state.twoFactor = action.payload;
    },
    twoFactorCleared: (state) => {
      state.twoFactor = null;
    },
    loginUserSuccess: (state, action) => {
      state.loading = false;
      state.isLoggedIn = true;
      state.authToken = action.payload.accessToken;
      state.error = null;
      state.user = action.payload.userDetails;
      state.data = action.payload;
    },
    loginUserFailure: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = action.payload;
      state.user = null;
      state.data = null;
      state._subdomain = null;
    },
    logoutUserAction: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = null;
      state.user = null;
      state.data = null;
      state.twoFactor = null;
      state._subdomain = null;
    },
    tokenAction: (state, action) => {
      localStorage.setItem("authToken", action.payload);
      state.authToken = action.payload;
    },
    overrideSubdomainAction: (state, action) => {
      localStorage.setItem("_subdomain", action.payload);
      state._subdomain = action.payload;
    },
    getUserStart: (state) => {
      // Refresh-in-flight: only flip loading. Do NOT clear user/data/
      // isLoggedIn — the React tree mid-mounted picker calls
      // `dispatch(getUser())` after saving a system pick, and nulling
      // state.user during that window made `pickSidebarVariant(null)`
      // unmount the sidebar in App.js, which let the catch-all route
      // bounce the user to /login before getUserSuccess could land.
      // Initial login still works fine: getUserSuccess populates user
      // from null on first load.
      state.loading = true;
      state.error = null;
    },
    getUserSuccess: (state, action) => {
      state.loading = false;
      state.isLoggedIn = true;
      state.error = null;
      state.data = action.payload; 
      state.user = action.payload.userDetails;
    },
    getUserFailure: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = action.payload;
      state.user = null;
      state.data = null;
      state._subdomain = null;
    },
    loginReset: (state, action) => {
      state.loading = false;
      state.error = null;
    },

    loginWithEmailStart: (state) => {
      state.loginWithEmail.loading = true;
      state.loginWithEmail.error = null;
      state.loginWithEmail.user = null;
    },
    loginWithEmailSuccess: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = null;
      state.loginWithEmail.data = action.payload;
    },
    loginWithEmailFailure: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = action.payload;
      state.loginWithEmail.data = null;
    },
    loginWithEmailReset: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = null;
      state.loginWithEmail.data = null;
    },

    handleUnAuthorizedAction: (state, action) => {
      state.handleUnAuthorized.redirectToLogin = action.payload;
    },
  },
});

export const {
  loginUserStart,
  loginUserSuccess,
  loginUserFailure,
  tokenAction,
  logoutUserAction,
  getUserStart,
  getUserSuccess,
  getUserFailure,
  loginReset,
  loginWithEmailStart,
  loginWithEmailSuccess,
  loginWithEmailFailure,
  loginWithEmailReset,
  handleUnAuthorizedAction,
  overrideSubdomainAction,
  twoFactorRequired,
  twoFactorCleared,
} = authSlice.actions;

export default authSlice.reducer;
